import type { Point2 } from './viewTypes'

// ---------------------------------------------------------------------------
// Anotações do desenho (quarta leva).
//
// Uma anotação é uma etiqueta de texto ancorada a um ponto do DESENHO
// (metros no plano da vista, as mesmas coordenadas das polilinhas). Não
// é geometria do modelo: nasce de um clique do utilizador e viaja com a
// vista gravada.
//
// A COTA DO TEXTO declara-se em MILÍMETROS DE PAPEL, não em metros de
// modelo: é assim que se especifica texto em desenho técnico (2,5 mm é
// a altura corrente de uma legenda ISO 3098). Cada writer converte para
// a sua unidade:
//   - SVG: a unidade é o mm de papel, portanto a altura vai directa;
//   - PDF: mm -> pontos (x 72/25,4);
//   - DXF: o model space é 1:1 em metros, portanto a altura em unidades
//     do desenho é `sizeMm/1000 * scaleDenom` (2,5 mm a 1:100 = 0,25 m).
//
// Puro: só números e strings, testável em Node.
// ---------------------------------------------------------------------------

export interface Annotation2D {
  id: string
  /** Âncora, em coordenadas do desenho (metros no plano da vista). */
  x: number
  y: number
  /** Uma linha. Os fins de linha são removidos na criação. */
  text: string
  /** Altura do texto em milímetros de PAPEL. Ausente = ANNOT_SIZE_MM. */
  sizeMm?: number
}

/** Altura por omissão do texto, em mm de papel. */
export const ANNOT_SIZE_MM = 2.5

/** Camada própria das anotações (DXF e agrupamento no SVG). O nome já
 *  está em maiúsculas e sem acentos: é seguro como nome de layer R12. */
export const ANNOT_LAYER = 'ANOTACOES'

/** Cor por omissão do texto. */
export const ANNOT_COLOR = '#111111'

/** Comprimento máximo do texto de uma anotação. Um desenho não é um
 *  documento de texto, e sem limite uma colagem acidental enchia o
 *  localStorage. */
export const ANNOT_MAX_LEN = 120

export function newAnnotationId(now = Date.now(), rand = Math.random()): string {
  return `a2d_${now.toString(36)}_${Math.floor(rand * 1e6).toString(36)}`
}

/** Normaliza o texto de uma anotação: uma linha, sem espaços a mais nas
 *  pontas, truncado. */
export function cleanAnnotationText(raw: string): string {
  return String(raw ?? '')
    .replace(/[\r\n\t]+/g, ' ')
    .trim()
    .slice(0, ANNOT_MAX_LEN)
}

/** Cria uma anotação. Devolve null se o texto ficar vazio (um clique
 *  sem escrever nada não deve deixar etiqueta invisível no desenho). */
export function makeAnnotation(
  at: Point2,
  text: string,
  id: string,
  sizeMm?: number,
): Annotation2D | null {
  const clean = cleanAnnotationText(text)
  if (!clean) return null
  if (!Number.isFinite(at[0]) || !Number.isFinite(at[1])) return null
  const out: Annotation2D = { id, x: at[0], y: at[1], text: clean }
  if (Number.isFinite(sizeMm) && (sizeMm as number) > 0) out.sizeMm = sizeMm
  return out
}

export function addAnnotation(
  list: Annotation2D[],
  a: Annotation2D,
): Annotation2D[] {
  return [...list.filter((v) => v.id !== a.id), a]
}

export function removeAnnotation(
  list: Annotation2D[],
  id: string,
): Annotation2D[] {
  return list.filter((v) => v.id !== id)
}

export function moveAnnotation(
  list: Annotation2D[],
  id: string,
  x: number,
  y: number,
): Annotation2D[] {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return list
  return list.map((v) => (v.id === id ? { ...v, x, y } : v))
}

/** Muda o texto. Texto vazio APAGA a anotação (é o gesto natural: quem
 *  limpa a caixa de edição quer-se ver livre da etiqueta). */
export function setAnnotationText(
  list: Annotation2D[],
  id: string,
  text: string,
): Annotation2D[] {
  const clean = cleanAnnotationText(text)
  if (!clean) return removeAnnotation(list, id)
  return list.map((v) => (v.id === id ? { ...v, text: clean } : v))
}

/** Altura do texto em UNIDADES DO DESENHO (metros de modelo) para uma
 *  dada escala de papel. */
export function annotationHeightM(
  sizeMm: number | undefined,
  scaleDenom: number,
): number {
  const mm = Number.isFinite(sizeMm) && (sizeMm as number) > 0
    ? (sizeMm as number)
    : ANNOT_SIZE_MM
  const denom = Number.isFinite(scaleDenom) && scaleDenom > 0 ? scaleDenom : 100
  return (mm / 1000) * denom
}

/** Altura do texto em mm de papel (com o valor por omissão aplicado). */
export function annotationSizeMm(a: Annotation2D): number {
  return Number.isFinite(a.sizeMm) && (a.sizeMm as number) > 0
    ? (a.sizeMm as number)
    : ANNOT_SIZE_MM
}

/** Filtra uma lista vinda de JSON (vista gravada de outra versão,
 *  localStorage mexido à mão). Devolve undefined quando não sobra nada,
 *  para o campo poder ser simplesmente omitido. */
export function sanitizeAnnotations(
  raw: unknown,
): Annotation2D[] | undefined {
  if (!Array.isArray(raw)) return undefined
  const out: Annotation2D[] = []
  const seen = new Set<string>()
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const o = item as Record<string, unknown>
    if (typeof o.id !== 'string' || o.id === '' || seen.has(o.id)) continue
    if (typeof o.x !== 'number' || !Number.isFinite(o.x)) continue
    if (typeof o.y !== 'number' || !Number.isFinite(o.y)) continue
    const text = cleanAnnotationText(String(o.text ?? ''))
    if (!text) continue
    seen.add(o.id)
    const a: Annotation2D = { id: o.id, x: o.x, y: o.y, text }
    if (typeof o.sizeMm === 'number' && Number.isFinite(o.sizeMm) && o.sizeMm > 0) {
      a.sizeMm = o.sizeMm
    }
    out.push(a)
  }
  return out.length > 0 ? out : undefined
}
